/**
 * FAST v3.0 (Founder / Advisor Standard Template, July 2026).
 * Source: https://fi.co/fast. Articles and schedules use the Version 3.0
 * text. Blanks (company, date, law, service level, percent, vesting length)
 * are filled from the draft. Restricted stock swaps the option sentences
 * for a restricted stock purchase. Startups.com hosts and e-signs it.
 */

import { FAST_MATRIX } from '@src/components/sco/Advisors/data/compCalculator'

export const FAST_VERSION = '3.0'
export const FAST_SECURITY_TYPE_NSO = 'nso'
export const FAST_SECURITY_TYPE_RSA = 'rsa'
export const FAST_SECURITY_TYPES = [
  { key: FAST_SECURITY_TYPE_NSO, title: 'Non-Qualified Stock Options' },
  { key: FAST_SECURITY_TYPE_RSA, title: 'Restricted Stock' },
] as const
export type FastSecurityTypeKey = (typeof FAST_SECURITY_TYPES)[number]['key']
/** Display label for the FAST v3 default (NSO). Prefer `fastSecurityTypeLabel`. */
export const FAST_SECURITY_TYPE = FAST_SECURITY_TYPES[0].title
export const FAST_TERMINATION_NOTICE_DAYS = 5
export const FAST_BOARD_AUTHORIZATION_DAYS = 90
export const FAST_HOURS_RANGE_LABEL = '5–10 hours per month'
export const FAST_LEGAL_RECAP =
  'Advisor NDA · IP assigned · 5-day notice · independent contractor · acceleration on sale'

export const FAST_COMPANY_STAGE_OPTIONS = ['Pre-seed', 'Seed', 'Series A'] as const
export const FAST_CONTRIBUTION_TIER_OPTIONS = ['Standard', 'Strategic', 'Expert'] as const

/** FAST v3 Schedule A time commitment is 5–10 hrs/mo at every service level. */
export const FAST_HOURS_PER_MONTH_DEFAULT = 5
export const FAST_HOURS_PER_MONTH_OPTIONS = [5, 8, 10] as const

/** Typical advisor-grant lengths; FAST v3 is 24 months. */
export const FAST_VESTING_MONTHS_OPTIONS = [12, 24, 36] as const

/** FAST v3 cliff is 3 months; 0 / 6 / 12 are the usual founder overrides. */
export const FAST_CLIFF_MONTHS_OPTIONS = [0, 3, 6, 12] as const

/**
 * The agreement has no contractual term (5-day notice). This is the expected
 * relationship length shown on the draft — 24 months matches the vest.
 */
export const FAST_TERM_MONTHS_OPTIONS = [12, 24, 36] as const

/** Most US startups using NSOs pick Delaware. FAST falls back to incorporation. */
export const FAST_GOVERNING_LAW_RECOMMENDED = 'Delaware'
export const FAST_GOVERNING_LAW_INCORPORATION = "the jurisdiction of the Company's incorporation"

const US_JURISDICTIONS = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
] as const

export const FAST_GOVERNING_LAW_OPTIONS = [
  FAST_GOVERNING_LAW_RECOMMENDED,
  ...US_JURISDICTIONS.filter(name => name !== FAST_GOVERNING_LAW_RECOMMENDED),
  FAST_GOVERNING_LAW_INCORPORATION,
] as const

export const FAST_TIER_BLURBS: Record<string, string> = {
  standard:
    'Top 50%: 5–10 hrs/mo, quarterly strategy and advisory-board meetings, reasonable email, and active introductions.',
  strategic:
    'Top 25%: 5–10 hrs/mo, monthly strategy meetings plus at least one customer/investor/partner meeting, fast email, and recruiting help.',
  expert:
    'Top 10%: 5–10 hrs/mo, bi-monthly strategy and project meetings, fast email and phone, marquee introductions, and at least one strategic project.',
}

export const FAST_STAGE_BLURBS: Record<string, string> = {
  idea: '1–3 cofounders, little or no revenue, MVP or mockups, friends-and-family capital.',
  startup: 'Full-time founders, LOIs or initial revenue, product validated by paying customers.',
  growth: 'Employees, significant traction and revenue, commercially scalable product.',
}

export const FAST_SECURITY_TYPE_BLURBS: Record<FastSecurityTypeKey, string> = {
  nso: 'FAST v3 default. Share count and exercise price are set later in board-approved option paperwork.',
  rsa: 'Actual shares at grant, typically before a 409A. Advisor should file an 83(b) election within 30 days.',
}

export const FAST_FIELD_HELPERS = {
  founderEmail: 'Where we send the company’s signing link.',
  founderCompany: 'Optional. Type the company name to use if none is matched to the founder.',
  advisorName: 'Person who signs, even if the grant is issued to a company.',
  advisorEmail: 'Where we send the advisor’s signing link.',
  advisorCompany:
    'Optional. If the FAST is issued to an entity, the advisor signs as its authorized representative.',
  companyStage: 'Used with service level to recommend equity.',
  securityType: 'This paper promises a later board-approved grant. It does not issue shares.',
  effectiveDate: 'Vesting starts here. Default is the first of next month.',
  hoursPerMonth: 'FAST standard is 5–10 hours per month at every service level. Expected time, not a cap.',
  termMonths: 'Shown on the draft. Either party may end the agreement on 5 days’ written notice.',
  equityPercent: 'FAST recommends from stage × service level; you can override.',
  vestingMonths: 'Shares vest monthly over this period.',
  additionalTerms: 'Optional. Appended after Miscellaneous. Does not change the FAST standard terms.',
} as const

export const FAST_ADDITIONAL_TERMS_MAX_LENGTH = 4000

export const normalizeAdditionalTerms = (value?: string | null): string => {
  if (!value) {
    return ''
  }
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .slice(0, FAST_ADDITIONAL_TERMS_MAX_LENGTH)
}

export const normalizeFastSecurityTypeKey = (value?: string | null): FastSecurityTypeKey => {
  const trimmed = String(value || '')
    .trim()
    .toLowerCase()
  if (!trimmed) {
    return FAST_SECURITY_TYPE_NSO
  }
  if (trimmed === FAST_SECURITY_TYPE_RSA || trimmed.includes('restricted stock')) {
    return FAST_SECURITY_TYPE_RSA
  }
  return FAST_SECURITY_TYPE_NSO
}

export const fastSecurityTypeLabel = (value?: string | null): string => {
  const key = normalizeFastSecurityTypeKey(value)
  return FAST_SECURITY_TYPES.find(item => item.key === key)?.title || FAST_SECURITY_TYPE
}

export const isFastRestrictedStock = (value?: string | null): boolean =>
  normalizeFastSecurityTypeKey(value) === FAST_SECURITY_TYPE_RSA

const counted = (n: number) => {
  const words: Record<number, string> = { 5: 'five', 90: 'ninety' }
  const word = words[n]
  return word ? `${word} (${n})` : String(n)
}

/** FAST Terms blank. NSO matches the Version 3.0 cell; RSA swaps options for shares. */
export const fastAgreementIntroGrantSentence = (value?: string | null): string =>
  isFastRestrictedStock(value)
    ? 'The exact number of shares shall be provided in the definitive Restricted Stock Purchase Agreement, which shall supersede this agreement.'
    : 'The exact number of options shall be provided in the definitive Stock Option Purchase Agreement, which shall supersede this agreement.'

export const fastAgreementCountLabel = (value?: string | null): string =>
  isFastRestrictedStock(value) ? 'Total Number of Shares' : 'Total Number of Stock Options'

export const fastAgreementPriceLabel = (value?: string | null): string =>
  isFastRestrictedStock(value) ? 'Purchase price' : 'Exercise Price'

/** Price cell. NSO matches Version 3.0; RSA uses the same price rule for a stock purchase. */
export const fastAgreementPriceSentence = (value?: string | null): string =>
  isFastRestrictedStock(value)
    ? 'The purchase price shall be equal to the fair market value of the Company\'s Common Stock at the "Effective Date". This will be documented in the applicable Restricted Stock Purchase Agreement.'
    : 'Exercise price shall be equal to the fair market value of the Company\'s Common Stock at the "Effective Date". This will be documented in the applicable Stock Option Purchase Agreement.'

const fastAgreementInstrumentPhrase = (value?: string | null) =>
  isFastRestrictedStock(value)
    ? 'definitive restricted stock purchase agreements'
    : 'definitive stock option purchase agreements'

/** Clause 2. Only the definitive-agreement name changes for restricted stock. */
export const fastAgreementCompensationGrantSentence = (value?: string | null): string =>
  `For the provision of the Services, Advisor shall be entitled to receive the compensation as detailed in the FAST Terms above. Percentages shall be based on the number of outstanding shares of Common Stock of the Company, calculated on a fully-diluted basis of all outstanding and convertible or issuable securities as of the Effective Date. The Company will seek written approval or have a meeting of the Board of Directors to authorize the Advisor compensation and deliver ${fastAgreementInstrumentPhrase(value)} regarding the stock compensation within ${counted(FAST_BOARD_AUTHORIZATION_DAYS)} days of this Agreement. If the Company fails to provide the foregoing documentation, then the Advisor shall have the right to contact directors of the Company.`

const vestPhrase = (label?: string | null) => {
  const raw = String(label || '').trim()
  if (!raw || raw === '—') {
    return 'a 2-year period'
  }
  if (/^24\s+months?$/.test(raw)) {
    return 'a 2-year period'
  }
  const months = raw.match(/^(\d+)\s+months?$/)
  if (months) {
    return `a ${months[1]}-month period`
  }
  return raw.startsWith('a ') ? raw : `a ${raw} period`
}

const cliffPhrase = (label?: string | null) => {
  const raw = String(label || '').trim()
  if (!raw || raw === '—') {
    return 'a 3-month cliff'
  }
  if (/^3\s+months?(?:\s+cliff)?$/.test(raw)) {
    return 'a 3-month cliff'
  }
  const months = raw.match(/^(\d+)\s+months?(?:\s+cliff)?$/)
  if (months) {
    return months[1] === '0' ? 'no cliff' : `a ${months[1]}-month cliff`
  }
  return raw.startsWith('a ') || raw.startsWith('no ') ? raw : `a ${raw}`
}

/** FAST Terms vesting cell. Defaults match Version 3.0; other lengths fill the same sentence. */
export const fastAgreementVestingSentence = (
  vestingMonthsLabel?: string | null,
  cliffMonthsLabel?: string | null
): string =>
  `All shares shall vest on a pro rata basis, monthly, over ${vestPhrase(vestingMonthsLabel)}, with ${cliffPhrase(cliffMonthsLabel)}. 100% of unvested shares shall vest on the sale of the Company.`

/** Services blank: level, and stage or expertise when the draft has them. */
export const fastAgreementServicesBlank = ({
  tier,
  stage,
  expertise,
}: {
  tier?: string | null
  stage?: string | null
  expertise?: string | null
} = {}): string => {
  const parts: string[] = []
  const level = String(tier || '').trim()
  const stageLabel = String(stage || '').trim()
  const area = String(expertise || '').trim()
  if (level && level !== '—') {
    parts.push(level)
  }
  if (stageLabel && stageLabel !== '—') {
    parts.push(stageLabel)
  }
  if (area && area !== '—' && area.toLowerCase() !== 'advisory services') {
    parts.push(area)
  }
  return parts.join(', ') || '—'
}

export const FAST_AGREEMENT_PREAMBLE =
  'This Founder Advisor Standard Template (this "Agreement") is entered into as of the date set forth on the signature page by and between the undersigned company (the "Company") and the undersigned advisor (the "Advisor"). This FAST is available at http://fi.co/fast, and the Company and the Advisor agree that neither one has modified the form, except to fill in blanks and bracketed terms.'

export type FastAgreementArticle = {
  number: string
  title: string
  body?: string
  children?: Array<{ number: string; title: string; body: string }>
}

const terminationBody = () =>
  `The term of this Agreement shall continue until terminated by either party for any reason upon ${counted(FAST_TERMINATION_NOTICE_DAYS)} days' prior written notice without further obligation or liability except for the compensation earned by the Advisor through such date of termination.`

/**
 * Version 3.0 articles in Founder Institute order.
 * Restricted stock swaps the option instrument name inside clause 2 only.
 */
export const fastAgreementArticles = (securityType?: string | null): FastAgreementArticle[] => [
  {
    number: '1',
    title: 'Services',
    body: 'Advisor agrees to act as a mentor or advisor to the Company and provide advice and assistance to the Company from time to time as further described on Schedule A attached hereto or as otherwise mutually agreed to by the parties (collectively, the "Services").',
  },
  {
    number: '2',
    title: 'Compensation',
    body: fastAgreementCompensationGrantSentence(securityType),
  },
  {
    number: '3',
    title: 'Expenses',
    body: 'Advisor shall not be reimbursed for expenses without prior written authorization of the Company following a detailed request by Advisor, which includes a maximum amount to be reimbursed.',
  },
  {
    number: '4',
    title: 'Term and Termination',
    body: terminationBody(),
  },
  {
    number: '5',
    title: 'Independent Contractor',
    body: "Advisor's relationship with the Company will be that of an independent contractor and not that of an employee. Advisor will not be eligible for any employee benefits, nor will the Company make deductions from payments made to Advisor for employment or income taxes, all of which will be Advisor's responsibility. Advisor will have no authority to enter into contracts that bind the Company or create obligations on the part of the Company without the prior written authorization of the Company.",
  },
  {
    number: '6',
    title: 'Nondisclosure of Confidential Information',
    children: [
      {
        number: 'a',
        title: 'Agreement Not to Disclose',
        body: "Advisor agrees not to use any Confidential Information (as defined below) disclosed to Advisor by the Company for Advisor's own use or for any purpose other than to carry out discussions concerning, and the undertaking of, the Services. Advisor shall use Confidential Information solely to perform the Services and shall protect it with reasonable care to prevent unauthorized use or disclosure. Upon the Company's request, Advisor will promptly return or destroy all materials containing Confidential Information.",
      },
      {
        number: 'b',
        title: 'Definition of Confidential Information',
        body: '"Confidential Information" means all non-public business, technical, or financial information disclosed by the Company to the Advisor, whether before or after the date of this Agreement, that is designated as confidential or should reasonably be understood to be confidential. This includes, without limitation, source code, inventions, product roadmaps, financial projections, and customer data. Confidential Information excludes information that: (i) was rightfully in Advisor\'s possession before disclosure; or (ii) becomes public through no fault of Advisor. Advisor may disclose information if required by law or court order, provided Advisor gives the Company prompt notice to allow for a protective order, or with the Company\'s prior written consent.',
      },
    ],
  },
  {
    number: '7',
    title: 'No Rights Granted',
    body: "Nothing in this Agreement shall be construed as granting any rights under any patent, copyright, or other intellectual property right of the Company, nor shall this Agreement grant Advisor any rights in or to the Company's Confidential Information, except the limited right to use the Confidential Information in connection with the Services.",
  },
  {
    number: '8',
    title: 'Assignment of Intellectual Property',
    body: 'To the extent that Advisor jointly or solely conceives, develops or reduces to practice any new inventions, original works of authorship, developments, concepts, know-how, improvements or trade secrets, whether or not patentable or registrable under copyright or similar laws or other intellectual property which would be deemed to be Confidential Information of the Company (collectively, "Intellectual Property") which clearly relates to the Company\'s business or technology and has been created by Advisor in the course of the performance of Services, Advisor hereby acknowledges that it is "work made for hire" for the benefit of the Company and hereby assigns all rights, titles and interest to such Intellectual Property to the Company.',
  },
  {
    number: '9',
    title: 'Duty to Assist',
    body: "As reasonably requested by the Company and only with respect to Intellectual Property created by Advisor for the Company as provided in paragraph 8 above, Advisor shall take all steps reasonably necessary to assist the Company in obtaining and enforcing in its own name any such Intellectual Property right. Advisor's obligation to assist the Company shall continue beyond the termination of Advisor's relationship with the Company.",
  },
  {
    number: '10',
    title: "Company's Right to Disclose",
    body: "During the term of this Agreement, the Company shall have the right to disclose the existence of this Agreement, Advisor's status as an Advisor, and to include Advisor's name, image and profile in various promotional materials, including, but not limited to, private placement memos or other offering materials, executive summaries, and the Company's world wide web page.",
  },
  {
    number: '11',
    title: 'No Conflicts',
    body: "Advisor represents that Advisor's compliance with the terms of this Agreement and provision of Services hereunder will not violate any duty which Advisor may have to any other person or entity (such as a present or former employer), and Advisor agrees that Advisor will not do anything in the performance of Services hereunder that would violate any such duty. In addition, Advisor agrees that, during the term of this Agreement, Advisor shall promptly notify the Company in writing of any competitor of the Company for which Advisor is also performing services.",
  },
  {
    number: '12',
    title: 'Miscellaneous',
    body: 'Any term of this Agreement may be amended or waived only with the written consent of the parties. This Agreement, including any schedules hereto, constitutes the sole agreement of the parties and supersedes all oral negotiations and prior writings with respect to the subject matter hereof. The validity, interpretation, construction, and performance of this Agreement shall be governed by the laws of the jurisdiction listed under "FAST Terms", without giving effect to the principles of conflict of laws. This Agreement may be executed in counterparts, each of which shall be deemed an original, but all of which together will constitute one and the same instrument.',
  },
]

export type FastAgreementSchedule = {
  intro: string
  levels: Array<{
    title: string
    commitment: string[]
    services: string[]
    compensation: string[]
  }>
  stageIntro: string
  stages: Array<{ title: string; characteristics: string[] }>
  benchmarksNote: string
}

const schedulePercent = (n: number) => `${Number(n).toFixed(2)}%`

const scheduleCompensation = (tierKey: 'Standard' | 'Strategic' | 'Expert') =>
  [
    { key: 'Pre-seed', title: 'Pre-Seed' },
    { key: 'Seed', title: 'Seed' },
    { key: 'Series A', title: 'Series A' },
  ].map(stage => `${stage.title} Stage: ${schedulePercent(FAST_MATRIX[stage.key][tierKey])}`)

/** Schedule A and Schedule B from Version 3.0. Percents come from FAST_MATRIX. */
export const fastAgreementSchedule = (): FastAgreementSchedule => ({
  intro:
    'Use the following table to determine the level of Advisor Compensation and Services, then write the chosen percentage in the "FAST Terms" table on page one. Percentages shall be based on the number of outstanding shares of Common Stock of the Company, calculated on a fully-diluted basis of all outstanding and convertible or issuable securities as of the date the Board of Directors approves the foregoing equity compensation. The exact number of shares shall be provided in the definitive document, which shall supersede this provision.',
  levels: [
    {
      title: 'Standard Performance Level - Top 50% Percentile of Advisors',
      commitment: [
        'Time: 5 - 10 hours.',
        'Meetings: Attend quarterly strategy meetings and scheduled Advisory Board meetings.',
        'Responsiveness: Provide a reasonable response to email requests by the Company.',
      ],
      services: [
        "Promotion: On top of the regular advice and insights, Advisor agrees to actively promote and make introductions on behalf of the Company through Advisor's overall network of business contacts, including forwarding the Company's business plan and other materials as requested by the Company.",
      ],
      compensation: scheduleCompensation('Standard'),
    },
    {
      title: 'Strategic Performance Level - Top 25% Percentile of Advisors',
      commitment: [
        'Time: 5 - 10 hours.',
        'Meetings: Attend monthly strategy meetings, scheduled Advisory Board meetings, and at least one meeting with a potential customer, investor, strategic partner, vendor, or employee.',
        'Responsiveness: Provide quick responses to email requests from the Company and from others associated with the Company.',
      ],
      services: [
        'Standard Performance plus:',
        "Recruiting: Advisor agrees to assist Company in identifying additional potential founding team members and employees through the Advisor's network of business contacts.",
      ],
      compensation: scheduleCompensation('Strategic'),
    },
    {
      title: 'Expert Performance Level - Top 10% Percentile of Advisors',
      commitment: [
        'Time: 5 - 10 hours.',
        'Meetings: Attend bi-monthly strategy meetings, scheduled Advisory Board meetings, internal project-related meetings, and meetings with potential customers, investors, strategic partners, vendors, or employees.',
        'Responsiveness: Provide quick responses to email requests and phone calls from the Company and from others associated with the Company.',
      ],
      services: [
        'Strategic Performance plus:',
        'Contacts: Advisor agrees to make introductions to and assist in the acquisition of marquee customers, strategic partners, and key industry contacts, and attend meetings with such potential customers, partners, and key contacts.',
        'Projects: Advisor agrees to assist the Company on at least one strategic project as requested by the Company during the term of this Agreement.',
      ],
      compensation: scheduleCompensation('Expert'),
    },
  ],
  stageIntro: 'The Company Stage is determined in accordance with the guidelines below.',
  stages: [
    {
      title: 'Pre-Seed',
      characteristics: [
        'Team: The team consists of one to three cofounders.',
        'Customers: The company is in discussions with potential customers to determine demand in the market. The pricing/revenue structure has been developed, but needs market validation.',
        'Revenue: The company has little to no revenue.',
        'Investors: At least one group consisting of the founder(s), their friends, or family has invested.',
        'Product: The Company has a minimum viable product, or product mockups, including wireframes and system designs.',
      ],
    },
    {
      title: 'Seed',
      characteristics: [
        'Team: The team consists of full-time founder(s) and is in the process of hiring initial employees as needed.',
        'Customers: The company has received letters of intent or customer commitments, and the market need has been validated.',
        'Revenue: The company has initial revenue.',
        'Investors: Investment may have been raised from friends/family or professional investors (e.g., angel, venture capital).',
        'Product: The product is built and validated by paying customers.',
      ],
    },
    {
      title: 'Series A',
      characteristics: [
        'Team: The team consists of full-time founder(s) and has hired employees.',
        'Customers: The company has achieved significant traction and user-based growth.',
        'Revenue: The company is collecting significant revenue.',
        'Investors: Prior investment may have been raised, and the founders are prepared to pitch to professional investors if additional capital is needed.',
        'Product: The Company has built a commercially scalable product.',
      ],
    },
  ],
  benchmarksNote: 'For more details on benchmarks of the various stages, visit: http://fi.co/benchmarks',
})

export type FastLegalTerms = {
  governingLaw?: string | null
  securityType?: string | null
  additionalTerms?: string | null
  terminationNoticeDays?: number | null
  boardAuthorizationDays?: number | null
  accelerationOnSale?: boolean | null
  version?: string | null
}

export const defaultFastLegalTerms = (overrides: FastLegalTerms = {}): FastLegalTerms => ({
  governingLaw: overrides.governingLaw ?? null,
  securityType: normalizeFastSecurityTypeKey(overrides.securityType),
  additionalTerms: normalizeAdditionalTerms(overrides.additionalTerms).trim() || null,
  terminationNoticeDays: overrides.terminationNoticeDays ?? FAST_TERMINATION_NOTICE_DAYS,
  boardAuthorizationDays: overrides.boardAuthorizationDays ?? FAST_BOARD_AUTHORIZATION_DAYS,
  accelerationOnSale: overrides.accelerationOnSale ?? true,
  version: overrides.version || FAST_VERSION,
})

export const parseFastLegalTerms = (raw: unknown): FastLegalTerms => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return defaultFastLegalTerms()
  }
  const value = raw as Record<string, unknown>
  return defaultFastLegalTerms({
    governingLaw: typeof value.governingLaw === 'string' ? value.governingLaw : null,
    securityType: typeof value.securityType === 'string' ? value.securityType : null,
    additionalTerms: typeof value.additionalTerms === 'string' ? value.additionalTerms : null,
    terminationNoticeDays:
      typeof value.terminationNoticeDays === 'number' ? value.terminationNoticeDays : null,
    boardAuthorizationDays:
      typeof value.boardAuthorizationDays === 'number' ? value.boardAuthorizationDays : null,
    accelerationOnSale: typeof value.accelerationOnSale === 'boolean' ? value.accelerationOnSale : null,
    version: typeof value.version === 'string' ? value.version : null,
  })
}
